/**
 * PDF report cover-page generator.
 *
 * Why this exists as its own module instead of being captured by html2canvas
 * along with the rest of the report:
 *
 * The chart pages of a report are happily captured from the live React
 * tree by html2canvas-pro and sliced into PDF pages. The cover page is
 * different — it relies on a brand-specific cover image (often a complex
 * SVG with `object-fit: cover` and `object-position: bottom left`) plus
 * overlay text whose layout depends on the dashboard's CSS-grid metrics.
 * That combination repeatedly broke in the html2canvas pipeline:
 *
 *   - html2canvas-pro doesn't reliably honour `object-fit` /
 *     `object-position` on `<img>` SVG sources, so the cover image came
 *     out cropped, oversized, or shifted.
 *   - Inline `<svg>` rendering inside html2canvas exposed clipPath /
 *     intrinsic-size quirks (the wilson SVG had an orphan offscreen path
 *     that leaked into the captured output).
 *   - The dashboard's live ContentGrid registers itself as the export root
 *     and is sized to the user's viewport, so the captured cover came out
 *     at viewport width rather than the PDF's logical 850 px page width —
 *     bigger screen meant bigger cover, content drifted, params bled.
 *
 * Drawing the cover directly with jsPDF primitives sidesteps all of this:
 * background colour, brand cover image, brand report logo, title,
 * description, and parameters are positioned by exact PT coordinates,
 * deterministic regardless of viewport size. The chart pages are still
 * captured normally; the exporter just skips the canvas's first slice
 * (which contained the cover) and uses this module for page 1 instead.
 *
 * Layout intent mirrors `ReportCoverPage.tsx` — the same title-at-22%-from-
 * top, description-below-title, parameters-in-two-columns shape — using
 * Source Sans 3 (the dashboard's heading + body font, declared as "Source
 * Sans Pro" in `libs/styles/src/lib/theme.ts`). Font files are fetched
 * lazily on first export and cached in module state.
 */
import type jsPDF from 'jspdf';

import { globeImage } from '@revelio/assets';

import sourceSansBoldUrl from '../../../../../../../assets/fonts/SourceSans3-Bold.ttf';
import sourceSansRegularUrl from '../../../../../../../assets/fonts/SourceSans3-Regular.ttf';
import { REPORT_THEME } from '../../../../PageConfigProvider/constants';

export interface PdfCoverParams {
  title: string;
  description: string;
  parameters: { label: string; value: string }[];
  brand: {
    displayName: string;
    coverImageUrl?: string;
    coverBackgroundColor?: string;
    coverTitleColor: string;
    coverDescriptionColor: string;
    /** Solid fallback when there's no cover image and no gradient. */
    fallbackBackgroundColor?: string;
    /**
     * Vertical-gradient fallback for brands without a cover image (mirrors
     * `linear-gradient(to bottom, sidebarColor[900], sidebarColor[500])` in
     * `ReportCoverPage.tsx`). Both must be set for the gradient to render;
     * otherwise the cover falls back to `fallbackBackgroundColor` or the
     * native page colour.
     */
    fallbackGradientFrom?: string;
    fallbackGradientTo?: string;
    reportLogoUrl?: string;
  };
}

const COVER_FONT_NAME = 'SourceSans3';

const pxToPt = (px: number): number => (px * 72) / 96;

// ---- font loading -------------------------------------------------------

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 0x8000) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + 0x8000))
    );
  }
  return btoa(binary);
};

let coverFontCache: { regular: string; bold: string } | null = null;

const fetchCoverFont = async (): Promise<{
  regular: string;
  bold: string;
} | null> => {
  if (coverFontCache) return coverFontCache;
  try {
    const [regularRes, boldRes] = await Promise.all([
      fetch(sourceSansRegularUrl),
      fetch(sourceSansBoldUrl),
    ]);
    const [regularBuf, boldBuf] = await Promise.all([
      regularRes.arrayBuffer(),
      boldRes.arrayBuffer(),
    ]);
    coverFontCache = {
      regular: arrayBufferToBase64(regularBuf),
      bold: arrayBufferToBase64(boldBuf),
    };
    return coverFontCache;
  } catch (err) {
    console.warn(
      '[pdf-cover] failed to load Source Sans font, falling back to Helvetica',
      err
    );
    return null;
  }
};

/**
 * Register Source Sans 3 (the dashboard's heading + body font) on the given
 * jsPDF instance. Returns the font name to use, or `'helvetica'` if loading
 * fails — same humanist-sans family, so layout proportions stay sensible
 * either way. Files are fetched/encoded once per page load and cached.
 */
const registerCoverFont = async (pdf: jsPDF): Promise<string> => {
  const data = await fetchCoverFont();
  if (!data) return 'helvetica';
  pdf.addFileToVFS(`${COVER_FONT_NAME}-Regular.ttf`, data.regular);
  pdf.addFont(`${COVER_FONT_NAME}-Regular.ttf`, COVER_FONT_NAME, 'normal');
  pdf.addFileToVFS(`${COVER_FONT_NAME}-Bold.ttf`, data.bold);
  pdf.addFont(`${COVER_FONT_NAME}-Bold.ttf`, COVER_FONT_NAME, 'bold');
  return COVER_FONT_NAME;
};

// ---- image loading ------------------------------------------------------

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });

/**
 * jsPDF's `addImage` works best with raster sources (PNG/JPEG). SVG sources
 * are flaky in jsPDF — no consistent natural dimensions, no rasterization.
 * Render the loaded image onto a 3×-DPR canvas first, then jsPDF embeds the
 * canvas as a proper raster.
 */
const loadImageAsCanvas = async (
  url: string,
  targetWidthPt: number,
  targetHeightPt: number
): Promise<HTMLCanvasElement> => {
  const img = await loadImage(url);
  const dpr = 3; // overshoot resolution so rasterized SVGs stay crisp
  const canvas = document.createElement('canvas');
  // jsPDF works in PT; canvas is logical px: pt * 96/72 = px, then * dpr.
  const widthPx = Math.round((targetWidthPt * 96) / 72) * dpr;
  const heightPx = Math.round((targetHeightPt * 96) / 72) * dpr;
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.drawImage(img, 0, 0, widthPx, heightPx);
  }
  return canvas;
};

// ---- colour helpers -----------------------------------------------------

const hexToRgb = (hex: string): [number, number, number] | null => {
  const m = hex.replace('#', '');
  if (m.length !== 3 && m.length !== 6) return null;
  const full =
    m.length === 3
      ? m
          .split('')
          .map((c) => c + c)
          .join('')
      : m;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return null;
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
};

const setFill = (pdf: jsPDF, color: string) => {
  const rgb = hexToRgb(color);
  if (rgb) pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
};
const setText = (pdf: jsPDF, color: string) => {
  const rgb = hexToRgb(color);
  if (rgb) pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
};

/**
 * Draw a vertical linear gradient by rasterising it on a canvas and embedding
 * the canvas via `pdf.addImage`. jsPDF has no native gradient API, and the
 * naive "stack N rect stripes" approach bands visibly because adjacent
 * stripes round to the same RGB triple and then jump by 1 — the canvas
 * `createLinearGradient` does sub-pixel interpolation (and dithering) so the
 * embedded raster looks smooth at any zoom. PNG compresses a two-stop
 * gradient to a few KB, so the file-size cost is negligible.
 */
const drawVerticalGradient = (
  pdf: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  fromColor: string,
  toColor: string
) => {
  const dpr = 2;
  const widthPx = Math.max(1, Math.round((w * 96) / 72) * dpr);
  const heightPx = Math.max(1, Math.round((h * 96) / 72) * dpr);
  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const grad = ctx.createLinearGradient(0, 0, 0, heightPx);
  grad.addColorStop(0, fromColor);
  grad.addColorStop(1, toColor);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, widthPx, heightPx);
  pdf.addImage(canvas, 'PNG', x, y, w, h);
};

// ---- main entry ---------------------------------------------------------

/**
 * Render the report cover onto the given jsPDF page. The page must already
 * be created (this draws into the *current* page, doesn't add a new one).
 */
export const drawCoverPage = async (
  pdf: jsPDF,
  pageWidthPt: number,
  pageHeightPt: number,
  cover: PdfCoverParams
): Promise<void> => {
  const marginPt = pxToPt(REPORT_THEME.margins.pdf);
  // Chakra defaults: Heading line-height ~1.2, body Text line-height ~1.5.
  // Match per text block so the cover's vertical rhythm matches the dashboard.
  const titleLineHeight = 1.2;
  const bodyLineHeight = 1.5;

  const coverFont = await registerCoverFont(pdf);

  // Background fills the page (and shows through any image gaps). Order of
  // preference: brand cover-image background colour → vertical gradient →
  // solid fallback colour. The gradient mirrors the dashboard's
  // `linear-gradient(to bottom, sidebarColor[900], sidebarColor[500])`.
  if (cover.brand.coverBackgroundColor) {
    setFill(pdf, cover.brand.coverBackgroundColor);
    pdf.rect(0, 0, pageWidthPt, pageHeightPt, 'F');
  } else if (
    cover.brand.fallbackGradientFrom &&
    cover.brand.fallbackGradientTo
  ) {
    drawVerticalGradient(
      pdf,
      0,
      0,
      pageWidthPt,
      pageHeightPt,
      cover.brand.fallbackGradientFrom,
      cover.brand.fallbackGradientTo
    );
  } else if (cover.brand.fallbackBackgroundColor) {
    setFill(pdf, cover.brand.fallbackBackgroundColor);
    pdf.rect(0, 0, pageWidthPt, pageHeightPt, 'F');
  }

  // Brand cover image — sized to fill the page exactly. The brand asset
  // should already match the page's portrait aspect; jsPDF will stretch
  // any small mismatch. When no brand cover is provided, fall back to the
  // shared Revelio globe graphic anchored at the bottom-right of the page
  // (mirrors the default-brand layout in `ReportCoverPage.tsx`).
  if (cover.brand.coverImageUrl) {
    try {
      const canvas = await loadImageAsCanvas(
        cover.brand.coverImageUrl,
        pageWidthPt,
        pageHeightPt
      );
      pdf.addImage(canvas, 'PNG', 0, 0, pageWidthPt, pageHeightPt);
    } catch (err) {
      console.warn(
        '[pdf-cover] failed to load brand cover image, leaving background only',
        err
      );
    }
  } else {
    try {
      const sizingImg = await loadImage(globeImage);
      const aspect =
        sizingImg.naturalWidth > 0 && sizingImg.naturalHeight > 0
          ? sizingImg.naturalWidth / sizingImg.naturalHeight
          : 1364 / 544; // GLOBE_ASPECT_RATIO fallback
      // Match `ReportCoverPage`'s default-brand globe: 80% page width,
      // height auto via aspect, anchored bottom-right.
      const globeW = pageWidthPt * 0.8;
      const globeH = globeW / aspect;
      const globeX = pageWidthPt - globeW;
      const globeY = pageHeightPt - globeH;
      const globeCanvas = await loadImageAsCanvas(globeImage, globeW, globeH);
      pdf.addImage(globeCanvas, 'PNG', globeX, globeY, globeW, globeH);
    } catch (err) {
      console.warn(
        '[pdf-cover] failed to render fallback globe, leaving background only',
        err
      );
    }
  }

  // Brand report logo at the top-left (SVG-safe via canvas rasterization).
  if (cover.brand.reportLogoUrl) {
    try {
      const logoHpt = pxToPt(20);
      // Pre-load to learn aspect, then redraw at the right canvas size.
      const sizingImg = await loadImage(cover.brand.reportLogoUrl);
      const aspect =
        sizingImg.naturalWidth > 0 && sizingImg.naturalHeight > 0
          ? sizingImg.naturalWidth / sizingImg.naturalHeight
          : 4;
      const logoWpt = aspect * logoHpt;
      const logoCanvas = await loadImageAsCanvas(
        cover.brand.reportLogoUrl,
        logoWpt,
        logoHpt
      );
      pdf.addImage(logoCanvas, 'PNG', marginPt, marginPt, logoWpt, logoHpt);
    } catch {
      // logo missing is a soft failure — don't block export
    }
  }

  // Title, description, parameters mirror ReportCoverPage layout.
  const contentX = marginPt;
  const contentW = pageWidthPt - 2 * marginPt;
  const titleTopPt = pageHeightPt * 0.222; // = (8 / DEFAULT_GRID_ROWS) * 100%

  // Title.
  const titleFontSize = 32;
  pdf.setFont(coverFont, 'bold');
  pdf.setFontSize(titleFontSize);
  setText(pdf, cover.brand.coverTitleColor);
  const titleLines = pdf.splitTextToSize(cover.title || 'Title', contentW);
  // jsPDF baseline = first-line bottom; offset by font size so titleTopPt
  // becomes the visual top of the text block.
  pdf.text(titleLines, contentX, titleTopPt + titleFontSize, {
    lineHeightFactor: titleLineHeight,
  });
  const titleBlockH =
    titleFontSize + (titleLines.length - 1) * titleFontSize * titleLineHeight;

  // Description follows the title with a small visual gap (1rem residual
  // beyond the title block's line-height descent).
  const descFontSize = 11;
  pdf.setFont(coverFont, 'normal');
  pdf.setFontSize(descFontSize);
  setText(pdf, cover.brand.coverDescriptionColor);
  const descTopPt = titleTopPt + titleBlockH + 8;
  const descLines = cover.description
    ? pdf.splitTextToSize(cover.description, contentW)
    : [];
  if (descLines.length > 0) {
    pdf.text(descLines, contentX, descTopPt + descFontSize, {
      lineHeightFactor: bodyLineHeight,
    });
  }
  const descBlockH =
    descLines.length > 0
      ? descFontSize + (descLines.length - 1) * descFontSize * bodyLineHeight
      : 0;

  // Parameters — two-column layout. The left column holds short values
  // (e.g. a single company name) and the right column holds long lists
  // (e.g. competitors). 30/70 split keeps the long list on one or two
  // lines instead of wrapping aggressively.
  const params = cover.parameters ?? [];
  if (params.length > 0) {
    const paramsTop = descTopPt + descBlockH + 12;
    const mid = Math.ceil(params.length / 2);
    const leftCol = params.slice(0, mid);
    const rightCol = params.slice(mid);
    const colGap = 24;
    const leftColW = (contentW - colGap) * 0.3;
    const rightColW = (contentW - colGap) * 0.7;

    const labelSize = 10;
    const valueSize = 10;
    const interParamGap = 8;

    const drawColumn = (
      entries: { label: string; value: string }[],
      colX: number,
      columnWidth: number
    ) => {
      let y = paramsTop;
      entries.forEach((p) => {
        pdf.setFont(coverFont, 'bold');
        pdf.setFontSize(labelSize);
        setText(pdf, cover.brand.coverTitleColor);
        pdf.text(p.label, colX, y + labelSize, {
          lineHeightFactor: bodyLineHeight,
        });
        y += labelSize + 2; // tight gap between label and value (matches dashboard)

        pdf.setFont(coverFont, 'normal');
        pdf.setFontSize(valueSize);
        setText(pdf, cover.brand.coverDescriptionColor);
        const valueLines = pdf.splitTextToSize(p.value, columnWidth);
        pdf.text(valueLines, colX, y + valueSize, {
          lineHeightFactor: bodyLineHeight,
        });
        const valueBlockH =
          valueSize + (valueLines.length - 1) * valueSize * bodyLineHeight;
        y += valueBlockH + interParamGap;
      });
    };

    drawColumn(leftCol, contentX, leftColW);
    if (rightCol.length > 0) {
      drawColumn(rightCol, contentX + leftColW + colGap, rightColW);
    }
  }
};
